import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
  EndUserType,
  MESSAGE_CONSTANTS,
  NIGHTWEB_APP_CONSTANT,
  NODATA_MESSAGES,
  STATUS_CODES,
} from 'src/app/constants';
import {
  OrganizationService,
  ThreatPairService,
  ToasterService,
  UserService,
} from 'src/app/services';
import { NightwebUtil } from 'src/app/utils';
import {
  ThreatPair,
  ThreatPairListStats,
  ThreatPairUser,
} from '../common/threat-pair-table/threat-pair-table.model';
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component';
import { AddEditThreatPairModalComponent } from '../modals/add-edit-threat-pair-modal/add-edit-threat-pair-modal.component';
import { ThreatPairAlertLogComponent } from '../modals/threat-pair-alert-log/threat-pair-alert-log.component';
import { Router } from '@angular/router';

@Component({
    selector: 'app-monitored-users',
    templateUrl: './monitored-users.component.html',
    standalone: false
})
export class MonitoredUsersComponent implements OnInit {
  monitoredUsersForm!: FormGroup;
  filterForm!: FormGroup;
  loading = false;
  includeInactive = false;
  endUserTypeList: {
    survivorList: { label: string; value: string }[];
    perpetratorList: { label: string; value: string }[];
  } = {
    survivorList: [],
    perpetratorList: [],
  };
  organizationList: { label: string; value: string }[] = [];

  organizationsFetchComplete = false;
  endUsersFetchComplete = false;

  statusFilterOptions = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  totalThreatPairsCount = 0;
  threatPairStats: ThreatPairListStats | null = null;

  limits = NIGHTWEB_APP_CONSTANT.showLimits;
  defaultLimitSelectWidth = NIGHTWEB_APP_CONSTANT.defaultLimitSelectWidth;

  threatPairs: ThreatPair[] = [];
  notFoundMessage = NODATA_MESSAGES.NO_THREAT_PAIRS;
  /** True while requesting the next page (append) — avoids duplicate loads when scroll fires often. */
  loadingMore = false;
  addEditThreatPairModalRef!: MdbModalRef<AddEditThreatPairModalComponent>;
  confirmationModalRef!: MdbModalRef<ConfirmationModalComponent>;
  config = NIGHTWEB_APP_CONSTANT.modalConfig;

  @ViewChild('tableElement', { static: false })
  tableElement!: ElementRef;

  @ViewChild('threatPairAlertLog')
  threatPairAlertLogPanel!: ThreatPairAlertLogComponent;

  constructor(
    private formBuilder: FormBuilder,
    private nightwebUtil: NightwebUtil,
    private modalService: MdbModalService,
    private router: Router,
    private organizationService: OrganizationService,
    private userService: UserService,
    private threatPairService: ThreatPairService,
    private toasterService: ToasterService,
  ) {
    this.setForm();
    this.setFilterForm();
  }

  get addThreatPairListsReady(): boolean {
    return this.organizationsFetchComplete && this.endUsersFetchComplete;
  }

  ngOnInit(): void {
    this.fetchAllOrganizations();
    this.fetchAllEndUsers();
    this.fetchThreatPairs();
  }

  fetchAllOrganizations(): void {
    this.organizationService
      .getAllOrganizations()
      .pipe(finalize(() => (this.organizationsFetchComplete = true)))
      .subscribe({
        next: (response: any) => {
          const rows = response?.data?.rows ?? [];
          this.organizationList = rows
            .filter((org: any) => org.id != null && String(org.id).length > 0)
            .map((org: any) => ({
              label: org.businessName || org.name || '—',
              value: String(org.id),
            }));
        },
        error: () => {},
      });
  }

  fetchAllEndUsers(): void {
    const mapRow = (user: any) => ({
      label:
        [user?.demographic?.firstName, user?.demographic?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim() || '—',
      value: String(user.id),
      organizationId: String(user.organizationId),
    });

    forkJoin({
      survivors: this.userService.getAllEndUsers({
        userType: EndUserType.Survivor,
      }),
      perpetrators: this.userService.getAllEndUsers({
        userType: EndUserType.Perpetrator,
      }),
    })
      .pipe(finalize(() => (this.endUsersFetchComplete = true)))
      .subscribe({
        next: ({ survivors, perpetrators }) => {
          const survRows = survivors?.data?.rows ?? [];
          const perpRows = perpetrators?.data?.rows ?? [];
          this.endUserTypeList.survivorList = survRows.map(mapRow);
          this.endUserTypeList.perpetratorList = perpRows.map(mapRow);
        },
        error: () => {
          this.endUserTypeList.survivorList = [];
          this.endUserTypeList.perpetratorList = [];
        },
      });
  }

  setForm(): void {
    this.monitoredUsersForm = this.formBuilder.group({
      keyword: [null],
      limit: [NIGHTWEB_APP_CONSTANT.defaultLimit],
      offset: [NIGHTWEB_APP_CONSTANT.defaultOffset],
    });
  }

  setFilterForm(): void {
    this.filterForm = this.formBuilder.group({
      survivorName: [null],
      perpetratorName: [null],
      organizationIds: [[] as string[]],
      status: [null],
    });
  }

  private buildThreatPairQuery(): Record<string, unknown> {
    const m = this.monitoredUsersForm.value;
    const f = this.filterForm.value;
    const merged: Record<string, unknown> = {
      keyword: m.keyword,
      limit: m.limit,
      offset: m.offset,
      survivorName: f.survivorName,
      perpetratorName: f.perpetratorName,
      status: f.status,
    };
    const rawOrgs = f.organizationIds;
    const organizationIds = Array.isArray(rawOrgs)
      ? rawOrgs
          .filter((id: unknown) => id != null && String(id).trim().length > 0)
          .map((id: unknown) => String(id))
      : rawOrgs != null && String(rawOrgs).trim().length > 0
        ? [String(rawOrgs)]
        : [];
    if (organizationIds.length > 0) {
      merged['organizationIds'] = organizationIds;
    }
    if (this.includeInactive) {
      merged['includeInactive'] = true;
    }
    return this.nightwebUtil.getFilterParams(merged);
  }

  private mapSideToPerson(side: ThreatPairUser | undefined): ThreatPairUser {
    if (!side) {
      return { initials: '—', name: '—', organization: { name: '—' } };
    }
    return {
      initials: side.initials ?? '—',
      name: side.name ?? '—',
      organization: side.organization,
    };
  }

  fetchThreatPairs(append = false): void {
    if (!append) {
      this.monitoredUsersForm.patchValue({
        offset: NIGHTWEB_APP_CONSTANT.defaultOffset,
        limit: NIGHTWEB_APP_CONSTANT.defaultLimit,
      });
      this.threatPairs = [];
    }
    const offset = this.monitoredUsersForm.value.offset;
    this.loading = !append && offset === NIGHTWEB_APP_CONSTANT.defaultOffset;

    const query = this.buildThreatPairQuery();
    this.threatPairService
      .getThreatPairs(query)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.loadingMore = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          const data = response?.data;
          const rows = data?.rows ?? [];
          this.totalThreatPairsCount = data?.count ?? 0;
          const stats = data?.stats as ThreatPairListStats | undefined;
          this.threatPairStats = stats
            ? {
                totalPairs: stats.totalPairs,
                activePairs: stats.activePairs ?? 0,
                alertsToday: stats.alertsToday ?? 0,
                inactivePairs: stats.inactivePairs ?? 0,
              }
            : null;

          if (rows.length > 0) {
            this.threatPairs =
              append && offset !== NIGHTWEB_APP_CONSTANT.defaultOffset
                ? [...this.threatPairs, ...rows]
                : rows;
          } else if (append) {
            // No rows for this page: stop asking for more.
            this.threatPairs = [...this.threatPairs];
            this.totalThreatPairsCount = this.threatPairs.length;
          } else {
            this.threatPairs = [];
          }
        },
        error: () => {
          if (!append) {
            this.threatPairs = [];
            this.totalThreatPairsCount = 0;
            this.threatPairStats = null;
          }
        },
      });
  }

  /** Table wrapper + window scroll (same as end-user). */
  @HostListener('window:scroll', ['$event'])
  onScroll(event: any): void {
    if (this.loading || this.loadingMore) {
      return;
    }
    if (this.tableElement) {
      const nativeElement = this.tableElement.nativeElement;
      const height = Math.round(
        event.target.clientHeight + nativeElement.scrollTop + 1,
      );
      if (height >= nativeElement.scrollHeight) {
        this.onScrollToBottom({ scrolledToBottom: true });
      }
    }
  }

  /** Load next page — same pattern as end-user `onScrollToBottom`. */
  onScrollToBottom(event: any): void {
    if (this.loading || this.loadingMore) {
      return;
    }
    if (this.threatPairs.length !== this.totalThreatPairsCount) {
      if (event.scrolledToBottom) {
        this.loadingMore = true;
        this.monitoredUsersForm.controls['offset'].setValue(
          this.monitoredUsersForm.value.offset + 1,
        );
        this.fetchThreatPairs(true);
      }
    }
  }

  threatPairRowIndexLabel(index: number): string {
    return (index + 1).toString().padStart(2, '0');
  }

  threatPairDropdownToggleId(pair: ThreatPair): string {
    return `threat-pair-dd-${pair.id}`;
  }

  receiveLimitSelected(event: any): void {
    if (event?.controlName === 'limit') {
      this.monitoredUsersForm.patchValue({
        offset: NIGHTWEB_APP_CONSTANT.defaultOffset,
        limit: event.value,
      });
      this.fetchThreatPairs();
    }
  }

  receiveSearchKeyword(keyword: string): void {
    if (this.loading) {
      return;
    }
    this.monitoredUsersForm.patchValue({
      keyword,
      offset: NIGHTWEB_APP_CONSTANT.defaultOffset,
    });
    this.fetchThreatPairs();
  }

  onIncludeInactiveChange(): void {
    this.monitoredUsersForm.patchValue({
      offset: NIGHTWEB_APP_CONSTANT.defaultOffset,
    });
    this.fetchThreatPairs();
  }

  applyFilter(): void {
    this.monitoredUsersForm.patchValue({
      offset: NIGHTWEB_APP_CONSTANT.defaultOffset,
    });
    this.fetchThreatPairs();
  }

  clearFilter(): void {
    this.filterForm.reset({
      survivorName: null,
      perpetratorName: null,
      organizationIds: [],
      status: null,
    });
    this.monitoredUsersForm.patchValue({
      offset: NIGHTWEB_APP_CONSTANT.defaultOffset,
    });
    this.fetchThreatPairs();
  }

  addThreatPair(): void {
    const modalConfig = this.nightwebUtil.shallowCopy(this.config);
    modalConfig.modalClass = 'modal-dialog-scrollable modal-md';
    modalConfig.data = {
      ...modalConfig.data,
      survivorUserList: this.endUserTypeList.survivorList,
      perpetratorUserList: this.endUserTypeList.perpetratorList,
      organizationList: this.organizationList,
    };
    this.addEditThreatPairModalRef = this.modalService.open(
      AddEditThreatPairModalComponent,
      modalConfig,
    );
    this.addEditThreatPairModalRef.onClose.subscribe((data: any) => {
      if (data) {
        this.fetchThreatPairs();
      }
    });
  }

  onAlerts(pair: ThreatPair, distanceParameter: string = 'meter'): void {
    this.threatPairAlertLogPanel.openAlerts(pair, distanceParameter);
  }

  onViewProfile(pair: ThreatPair): void {
    const survivorId = pair.survivor._id;
    this.router.navigate(['/end-users-v2', survivorId]);
  }

  onSetActive(pair: ThreatPair): void {
    this.openThreatPairStatusConfirm(pair, 'active');
  }

  onSetInactive(pair: ThreatPair): void {
    this.openThreatPairStatusConfirm(pair, 'inactive');
  }

  private openThreatPairStatusConfirm(
    pair: ThreatPair,
    nextStatus: 'active' | 'inactive',
  ): void {
    const surv = pair.survivor?.name ?? '—';
    const perp = pair.perpetrator?.name ?? '—';
    const config = this.nightwebUtil.shallowCopy(this.config);
    config.modalClass = 'modal-md';
    config.data.confirmButtonLabel =
      nextStatus === 'active' ? 'Set Active' : 'Set Inactive';
    config.data.title =
      nextStatus === 'active'
        ? 'Activate threat pair'
        : 'Deactivate threat pair';
    config.data.body = `Are you sure you want to set this pair to <strong>${nextStatus}</strong>?<br/><span class="text-muted small">${surv} ↔ ${perp}</span>`;
    config.data.formData = { confirmed: true };
    this.confirmationModalRef = this.modalService.open(
      ConfirmationModalComponent,
      config,
    );
    this.confirmationModalRef.onClose.subscribe((data: any) => {
      if (data) {
        this.applyThreatPairStatus(pair.id, nextStatus);
      }
    });
  }

  private applyThreatPairStatus(
    pairId: string,
    status: 'active' | 'inactive',
  ): void {
    this.threatPairService.patchThreatPairStatus(pairId, { status }).subscribe({
      next: (response: any) => {
        if (response?.statusCode == STATUS_CODES.OK) {
          this.toasterService.show(
            'Success',
            MESSAGE_CONSTANTS.THREAT_PAIR_STATUS_UPDATED,
          );
          this.threatPairs = this.threatPairs.map((pair: ThreatPair) => {
            if (pair.id === pairId) {
              return { ...pair, status: status };
            }
            return pair;
          });

          this.threatPairStats = {
            ...this.threatPairStats,
            activePairs:
              status === 'active'
                ? this.threatPairStats?.activePairs
                  ? this.threatPairStats?.activePairs + 1
                  : 1
                : this.threatPairStats?.activePairs
                  ? this.threatPairStats?.activePairs - 1
                  : 0,
            inactivePairs:
              status === 'inactive'
                ? this.threatPairStats?.inactivePairs
                  ? this.threatPairStats?.inactivePairs + 1
                  : 1
                : this.threatPairStats?.inactivePairs
                  ? this.threatPairStats?.inactivePairs - 1
                  : 0,
          };
        }
      },
      error: () => {},
    });
  }

  onEditPair(pair: ThreatPair): void {
    const modalConfig = this.nightwebUtil.shallowCopy(this.config);
    modalConfig.modalClass = 'modal-dialog-scrollable modal-md';
    modalConfig.data = {
      ...modalConfig.data,
      isEdit: true,
      survivorUserList: this.endUserTypeList.survivorList,
      perpetratorUserList: this.endUserTypeList.perpetratorList,
      organizationList: this.organizationList,
      editThreatPair: pair,
    };
    this.addEditThreatPairModalRef = this.modalService.open(
      AddEditThreatPairModalComponent,
      modalConfig,
    );
    this.addEditThreatPairModalRef.onClose.subscribe((data: any) => {
      if (data) {
        this.fetchThreatPairs();
      }
    });
  }

  onDeletePair(pair: ThreatPair): void {
    this.openThreatPairDeleteConfirm(pair);
  }

  private openThreatPairDeleteConfirm(pair: ThreatPair): void {
    const surv = pair.survivor?.name ?? '—';
    const perp = pair.perpetrator?.name ?? '—';
    const config = this.nightwebUtil.shallowCopy(this.config);
    config.modalClass = 'modal-md';
    config.data.confirmButtonLabel = 'Delete';
    config.data.title = 'Delete threat pair';
    config.data.body = `${MESSAGE_CONSTANTS.DELETE_CONFIRMATION}<br/><span class="text-muted small mt-2 d-inline-block">${surv} ↔ ${perp}</span>`;
    config.data.formData = { confirmed: true };
    this.confirmationModalRef = this.modalService.open(
      ConfirmationModalComponent,
      config,
    );
    this.confirmationModalRef.onClose.subscribe((data: any) => {
      if (data) {
        this.applyThreatPairDelete(pair.id);
      }
    });
  }

  private applyThreatPairDelete(pairId: string): void {
    this.threatPairService.deleteThreatPair(pairId).subscribe({
      next: (response: any) => {
        if (response?.statusCode == STATUS_CODES.OK) {
          this.toasterService.show(
            'Success',
            MESSAGE_CONSTANTS.THREAT_PAIR_REMOVED,
          );
          this.fetchThreatPairs();
        }
      },
      error: () => {},
    });
  }
}
